/**
 * The router and the route guard.
 *
 * Four session states, four different trees, and the order they are checked in is
 * the order the server checks its own chain — you cannot be refused a permission
 * before it is known whether the company is on.
 *
 *   loading       the boot frame: chrome, a skeleton, and **no menu items**
 *   failed        the boot failure: what went wrong, and a `RetryAction`
 *   anonymous     the login screen, at whatever URL was asked for
 *   authenticated the shell — unless the company gate is closed
 *
 * ── Deep links survive a signed-out visit ───────────────────────────────────
 *
 * An anonymous visitor to `/attendance` is shown the login screen **at
 * `/attendance`**, not redirected to `/login`. Nothing needs to remember where they
 * were going, because the URL never changed: when the session resolves, the same
 * path renders the real screen. `/login` remains the canonical path for a plain
 * "sign in" link, and an already-signed-in visitor to it is sent to `/`.
 *
 * ── Refusal is rendered, redirect is not ────────────────────────────────────
 *
 * A route the session may not open renders `PermissionDenied` **at that URL**
 * (`PRODUCT.md` §3 and §4). An *unknown* URL is a different thing — nobody was
 * refused anything — and that one does redirect to the session's first available
 * screen.
 */

import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router';

import { useSession } from './auth/SessionProvider';
import { useT } from './i18n';
import {
  canOpen,
  MENU_ITEMS,
  UI_STATES_ITEM,
  visibleMenuItems,
  type MenuItem,
} from './nav/menu';
import { AiInsightsScreen } from './screens/AiInsightsScreen';
import { AlertsScreen } from './screens/AlertsScreen';
import { AlertRulesScreen } from './screens/alerts/AlertRulesScreen';
import { AuditLogScreen } from './screens/AuditLogScreen';
import { AttendanceScreen } from './screens/AttendanceScreen';
import { CompanyBlockedScreen, blockedReason } from './screens/CompanyBlockedScreen';
import { DataRiskScreen } from './screens/DataRiskScreen';
import { DevicesScreen } from './screens/DevicesScreen';
import { IncidentsScreen } from './screens/IncidentsScreen';
import { LoginScreen } from './screens/LoginScreen';
import { MyActivityScreen } from './screens/MyActivityScreen';
import { MyDataScreen } from './screens/MyDataScreen';
import { OverviewScreen } from './screens/OverviewScreen';
import { PermissionGrantsScreen } from './screens/admin/PermissionGrantsScreen';
import { DsarQueueScreen } from './screens/admin/DsarQueueScreen';
import { RetentionScreen } from './screens/admin/RetentionScreen';
import { PoliciesScreen } from './screens/PoliciesScreen';
import { CompanySettingsScreen } from './screens/CompanySettingsScreen';
import { PolicyEditorScreen } from './screens/policies/PolicyEditorScreen';
import { FormulasScreen } from './screens/productivity/FormulasScreen';
import { ReportsScreen } from './screens/ReportsScreen';
import { JobsScreen } from './screens/recruiting/JobsScreen';
import { CandidatesScreen } from './screens/recruiting/CandidatesScreen';
import { PipelineScreen } from './screens/recruiting/PipelineScreen';
import { InterviewsScreen } from './screens/recruiting/InterviewsScreen';
import { OffersScreen } from './screens/recruiting/OffersScreen';
import { OutreachScreen } from './screens/recruiting/OutreachScreen';
import { SourcingScreen } from './screens/recruiting/SourcingScreen';
import { InboxScreen } from './screens/recruiting/InboxScreen';
import { EmployeeDocumentsScreen } from './screens/hr/EmployeeDocumentsScreen';
import { OnboardingScreen } from './screens/hr/OnboardingScreen';
import { LeaveScreen } from './screens/hr/LeaveScreen';
import { PerformanceScreen } from './screens/hr/PerformanceScreen';
import { PayrollScreen } from './screens/PayrollScreen';
import { GeoScreen } from './screens/geo/GeoScreen';
import { PresenceScreen } from './screens/PresenceScreen';
import { EffectivenessScreen } from './screens/EffectivenessScreen';
import { PeopleScreen } from './screens/PeopleScreen';
import { RosterScreen } from './screens/RosterScreen';
import { PlaceholderScreen } from './screens/PlaceholderScreen';
import { SelectCompanyScreen } from './screens/SelectCompanyScreen';
import { StatesReferenceScreen } from './screens/StatesReferenceScreen';
import { TimeTrackingScreen } from './screens/TimeTrackingScreen';
import { AppShell } from './shell/AppShell';
import { MiniTimer } from './timer/MiniTimer';
import { TimerProvider } from './timer/TimerProvider';
import { ScreenHeading } from './shell/ScreenHeading';
import { Sidebar } from './shell/Sidebar';
import { EmptyState } from './states/EmptyState';
import { PermissionDenied } from './states/PermissionDenied';
import { RetryAction } from './states/RetryAction';
import { Skeleton } from './states/Skeleton';
import { StatusNote } from './states/StatusNote';

/**
 * What is on screen while `GET /auth/me` is in flight.
 *
 * The brand and the layout, a sidebar of unlabelled bars, and a skeleton in `main`.
 * No menu items, because there is no honest way to guess which sixteen-item subset
 * this session gets — see `auth/SessionProvider.tsx`.
 */
function BootFrame(): ReactNode {
  const t = useT();
  return (
    <div className="wp-shell" data-testid="boot-frame">
      <div className="wp-shell__brand">{t('app.name')}</div>
      <header className="wp-shell__topbar" />
      <Sidebar permissions={null} />
      <main id="main" className="wp-shell__main" tabIndex={-1} aria-label={t('shell.mainLandmark')}>
        <Skeleton
          rows={4}
          variant="block"
          label={t('shell.loadingSession')}
          testId="boot-skeleton"
        />
      </main>
    </div>
  );
}

/** `/auth/me` did not answer — a transport failure or a 5xx, never a 401. */
function BootFailure(): ReactNode {
  const t = useT();
  const { error, refresh } = useSession();
  const message = error?.userSafe === true ? error.message : t('common.unknownError');
  return (
    <div className="wp-centred">
      <div className="wp-centred__inner wp-centred__inner--wide">
        <ScreenHeading>{t('shell.sessionFailed')}</ScreenHeading>
        <StatusNote
          severity="critical"
          role="alert"
          title={message}
          testId="boot-failure"
          actions={<RetryAction onRetry={refresh} testId="boot-retry" />}
        >
          {error === null ? null : (
            <p className="wp-small wp-muted">
              {t('common.errorCode')}: <code>{error.code}</code>
              {error.correlationId === null ? null : (
                <>
                  {' · '}
                  {t('common.correlationId')}: <code>{error.correlationId}</code>
                </>
              )}
            </p>
          )}
        </StatusNote>
      </div>
    </div>
  );
}

/**
 * `/` — the session's landing screen.
 *
 * The first menu item it can actually open, which is Overview for a manager and
 * Attendance for an employee. When the effective set opens nothing at all — one
 * `deny workforce.view_self` row is enough — there is nowhere to send them, so the
 * refusal is stated rather than bounced around the router.
 */
function Home(): ReactNode {
  const t = useT();
  const { user } = useSession();
  const items = visibleMenuItems(user?.permissions ?? []);
  const first = items[0];
  if (first === undefined) {
    return (
      <EmptyState
        title={t('nav.empty.title')}
        description={t('nav.empty.body')}
        testId="home-empty"
      />
    );
  }
  return <Navigate to={first.path} replace />;
}

/**
 * A refused route, rendered at the URL that was refused.
 *
 * The heading is the point of this wrapper. `PermissionDenied` is a shared component
 * that screens also render *inside* themselves, where the screen owns the `<h1>` —
 * so on a whole route it would leave a page with no heading at all, which is a page
 * a screen-reader user lands on with nothing to orient by, and which `AppShell`'s
 * route-change focus handler would have to fall back to `<main>` for.
 *
 * Naming the screen discloses nothing: the sixteen labels are spec 01 §3's fixed
 * list, identical for every tenant, and the person already typed the URL.
 */
function DeniedScreen({ item }: { readonly item: MenuItem }): ReactNode {
  const t = useT();
  return (
    <div className="wp-stack">
      <ScreenHeading>{t(item.labelKey)}</ScreenHeading>
      <PermissionDenied testId={`denied-${item.id}`} />
    </div>
  );
}

/**
 * The screens Tasks 42, 43, 44, 45, 48, 53 and 57 built, by menu id. Everything else is
 * a placeholder.
 *
 * A map rather than routes written out beside the `MENU_ITEMS.map` below, so a screen
 * cannot be mounted at a path whose permission gate was never consulted: the guard
 * runs once, for every item, and *then* this decides what the allowed branch renders.
 */
const SCREENS: Readonly<Record<string, () => ReactNode>> = {
  'ai-insights': () => <AiInsightsScreen />,
  alerts: () => <AlertsScreen />,
  'alert-rules': () => <AlertRulesScreen />,
  attendance: () => <AttendanceScreen />,
  'audit-log': () => <AuditLogScreen />,
  'data-risk': () => <DataRiskScreen />,
  devices: () => <DevicesScreen />,
  incidents: () => <IncidentsScreen />,
  'my-activity': () => <MyActivityScreen />,
  'my-data': () => <MyDataScreen />,
  overview: () => <OverviewScreen />,
  'admin-permissions': () => <PermissionGrantsScreen />,
  'admin-retention': () => <RetentionScreen />,
  'admin-dsar': () => <DsarQueueScreen />,
  policies: () => <PoliciesScreen />,
  'policy-collection': () => <PolicyEditorScreen />,
  'company-settings': () => <CompanySettingsScreen />,
  'productivity-formulas': () => <FormulasScreen />,
  reports: () => <ReportsScreen />,
  'recruiting-jobs': () => <JobsScreen />,
  candidates: () => <CandidatesScreen />,
  pipeline: () => <PipelineScreen />,
  interviews: () => <InterviewsScreen />,
  offers: () => <OffersScreen />,
  outreach: () => <OutreachScreen />,
  sourcing: () => <SourcingScreen />,
  inbox: () => <InboxScreen />,
  'hr-documents': () => <EmployeeDocumentsScreen />,
  onboarding: () => <OnboardingScreen />,
  'hr-leave': () => <LeaveScreen />,
  'hr-performance': () => <PerformanceScreen />,
  payroll: () => <PayrollScreen />,
  'time-tracking': () => <TimeTrackingScreen />,
  geo: () => <GeoScreen />,
  presence: () => <PresenceScreen />,
  effectiveness: () => <EffectivenessScreen />,
  people: () => <PeopleScreen />,
  roster: () => <RosterScreen />,
};

function AuthenticatedRoutes(): ReactNode {
  const { user, signOut } = useSession();
  if (user === null) return null;
  const permissions = user.permissions;

  return (
    // ── The timer's single source of truth, mounted above the router ──────────
    //
    // Above `<Routes>` **and** above `AppShell`, so the top bar's `MiniTimer` and the
    // panel on `/time-tracking` read one state through one clock, and neither is
    // unmounted by a route change. Inside the authenticated tree, so signing out
    // discards it rather than leaving a stopped timer belonging to nobody.
    <TimerProvider>
      <AppShell user={user} onSignOut={() => void signOut()} timerSlot={<MiniTimer />}>
        <Routes>
          <Route path="/" element={<Home />} />
          {/* Already signed in; the form would mint a second session for one person. */}
          <Route path="/login" element={<Navigate to="/" replace />} />
          {/* The gate is open, so these two screens have nothing to say. */}
          <Route path="/company-blocked" element={<Navigate to="/" replace />} />
          <Route path="/select-company" element={<Navigate to="/" replace />} />
          {/* Not one of the menu items, but gated by the same predicate they are
              (UI-12) — and refused in place, never redirected, exactly as they are. */}
          <Route
            path={UI_STATES_ITEM.path}
            element={
              canOpen(UI_STATES_ITEM, permissions) ? (
                <StatesReferenceScreen />
              ) : (
                <DeniedScreen item={UI_STATES_ITEM} />
              )
            }
          />
          {MENU_ITEMS.map((item) => {
            const screen = SCREENS[item.id];
            return (
              <Route
                key={item.id}
                path={item.path}
                element={
                  canOpen(item, permissions) ? (
                    screen === undefined ? (
                      <PlaceholderScreen item={item} />
                    ) : (
                      screen()
                    )
                  ) : (
                    // In place, at the URL that was refused. Never a redirect: a refusal
                    // the user cannot see is a bug report they cannot write.
                    <DeniedScreen item={item} />
                  )
                }
              />
            );
          })}
          {/* Unknown URL — nobody was refused anything, so this one may redirect. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell>
    </TimerProvider>
  );
}

export function App(): ReactNode {
  const { status, user } = useSession();

  if (status === 'loading') return <BootFrame />;
  if (status === 'failed') return <BootFailure />;
  if (status === 'anonymous' || user === null) {
    return (
      <Routes>
        <Route path="*" element={<LoginScreen />} />
      </Routes>
    );
  }

  // Step 2 of the 02 §11 chain, mirrored: the company gate is answered before any
  // permission is looked at, and it closes the whole dashboard rather than a screen.
  const blocked = blockedReason(user);
  if (blocked !== null) {
    return (
      <Routes>
        <Route path="/company-blocked" element={<CompanyBlockedScreen />} />
        <Route path="*" element={<Navigate to="/company-blocked" replace />} />
      </Routes>
    );
  }

  // A session with two or more memberships and none chosen reads `/auth/me` and
  // nothing else (API_CONTRACT §2.1), so there is no screen it could render.
  if (user.company_selection_required) {
    return (
      <Routes>
        <Route path="/select-company" element={<SelectCompanyScreen />} />
        <Route path="*" element={<Navigate to="/select-company" replace />} />
      </Routes>
    );
  }

  return <AuthenticatedRoutes />;
}
